import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ANOMALY_REASON_LABEL, AnomalyReason, IssueDetailDTO, TaskTransitionTimeline } from '../../entities/task-flow';
import { getJiraTaskDetail, getTaskTimeline } from '../../shared/api/dashboard';
import { getTaskRiskReasonMeta } from '../../shared/utils/risk-flag-labels';
import { fmt1 } from '../../shared/utils/stat-helpers';
import { TaskDetailShell } from '../../widgets/task-detail/TaskDetailShell';

// Whatever the row that linked here already had loaded (risky-tasks/anomalies tables in
// ProjectStatsPage) — used as an instant-render fallback while the real fetch below resolves, and
// as the only source for `url` (GET /api/dashboard/tasks/:issueId doesn't return one). Degrades
// gracefully if the page is reached directly (state doesn't survive a hard reload).
interface JiraTaskNavState {
  issueKey?: string;
  summary?: string;
  status?: string;
  assigneeName?: string;
  url?: string;
  riskScore?: number;
  riskReasons?: string[];
  anomalyReasons?: AnomalyReason[];
}

const JiraTaskDetailPage: React.FC = () => {
  const { projectId, issueId } = useParams<{ projectId: string; issueId: string }>();
  const location = useLocation();
  const navState = (location.state ?? {}) as JiraTaskNavState;

  const [task, setTask] = useState<IssueDetailDTO | null>(null);
  const [timeline, setTimeline] = useState<TaskTransitionTimeline | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  useEffect(() => {
    if (!issueId) return;
    let cancelled = false;
    setIsLoading(true);
    setDetailError(null);
    setTimelineError(null);

    Promise.allSettled([getJiraTaskDetail(issueId), getTaskTimeline(issueId)]).then(([detailRes, timelineRes]) => {
      if (cancelled) return;
      if (detailRes.status === 'fulfilled') setTask(detailRes.value.task);
      else setDetailError('Failed to load issue details.');
      if (timelineRes.status === 'fulfilled') setTimeline(timelineRes.value.timeline);
      else setTimelineError('Failed to load the status timeline.');
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [issueId]);

  const displayKey = task?.issueKey ?? navState.issueKey ?? issueId;
  const status = task?.status ?? timeline?.currentStatus ?? navState.status;
  const assigneeName = task?.assigneeName ?? navState.assigneeName;

  return (
    <TaskDetailShell
      backTo={`/dashboard/projects/${projectId}`}
      backLabel="Back to project"
      provider="jira"
      titleKey={displayKey}
      isLoading={isLoading}
      externalUrl={navState.url}
      externalLabel="Open in Jira"
    >
      <div className="card bg-base-100 shadow-card">
        <div className="card-body p-5 gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-semibold">{task?.summary ?? navState.summary ?? displayKey}</span>
            {status && <span className="badge badge-outline">{status}</span>}
            {task?.priority && <span className="badge badge-ghost">{task.priority}</span>}
            {!!timeline?.reopenCount && (
              <span className="badge badge-warning badge-sm">Reopened {timeline.reopenCount}x</span>
            )}
          </div>
          <div className="text-sm opacity-70 space-x-3">
            {assigneeName && <span>Assignee: {assigneeName}</span>}
            {task?.reporterName && <span>Reporter: {task.reporterName}</span>}
          </div>

          {!!task?.labels.length && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {task.labels.map((l) => (
                <span key={l} className="badge badge-ghost badge-sm">
                  {l}
                </span>
              ))}
            </div>
          )}

          {navState.riskScore != null && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <span
                className={`badge ${navState.riskScore >= 70 ? 'badge-error' : navState.riskScore >= 40 ? 'badge-warning' : 'badge-success'}`}
              >
                Risk {navState.riskScore}
              </span>
              {(navState.riskReasons ?? []).map((r) => (
                <span key={r} className="badge badge-ghost" title={getTaskRiskReasonMeta(r).description}>
                  {getTaskRiskReasonMeta(r).label}
                </span>
              ))}
            </div>
          )}

          {!!navState.anomalyReasons?.length && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              {navState.anomalyReasons.map((r) => (
                <span key={r} className="badge badge-ghost">
                  {ANOMALY_REASON_LABEL[r]}
                </span>
              ))}
            </div>
          )}

          {detailError && (
            <div className="alert alert-error py-2">
              <span className="text-sm">{detailError}</span>
            </div>
          )}
        </div>
      </div>

      {task && (
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-5 gap-3">
            <div className="font-semibold">Description</div>
            {task.description ? (
              <div className="text-sm whitespace-pre-wrap">{task.description}</div>
            ) : (
              <div className="text-sm opacity-50">No description.</div>
            )}
          </div>
        </div>
      )}

      {task && (
        <div className="card bg-base-100 shadow-card">
          <div className="card-body p-5 gap-3">
            <div className="font-semibold">Comments</div>
            {task.comments.length > 0 ? (
              <div className="space-y-1.5">
                {task.comments.map((c) => (
                  <div key={c.id} className="rounded-lg bg-base-200 px-3 py-2 text-sm space-y-1">
                    <div className="text-xs opacity-40">{new Date(c.createdAt).toLocaleString()}</div>
                    <div className="whitespace-pre-wrap">{c.text}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm opacity-50">
                Comments aren&apos;t synced from Jira yet — use &quot;Open in Jira&quot; above to read or add one.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card bg-base-100 shadow-card">
        <div className="card-body p-5 gap-3">
          <div className="font-semibold">Status timeline</div>
          {timelineError && (
            <div className="alert alert-error py-2">
              <span className="text-sm">{timelineError}</span>
            </div>
          )}
          {timeline && timeline.steps.length > 0 ? (
            <ol className="relative border-l border-base-300 ml-2 space-y-4 pt-1">
              {timeline.steps.map((step, i) => {
                const isActive = step.exitedAt === null;
                return (
                  <li key={i} className="ml-4">
                    <span
                      className={`absolute -left-[5px] w-2.5 h-2.5 rounded-full ${
                        isActive ? 'bg-primary' : 'bg-base-300'
                      }`}
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{step.status ?? 'Unknown'}</span>
                      {isActive && <span className="badge badge-primary badge-xs">current</span>}
                    </div>
                    <div className="text-xs opacity-60">
                      {new Date(step.enteredAt).toLocaleString()}
                      {step.exitedAt ? ` → ${new Date(step.exitedAt).toLocaleString()}` : ' → now'}
                    </div>
                    <div className="text-xs opacity-80 mt-0.5">{fmt1(step.durationDays)} days</div>
                  </li>
                );
              })}
            </ol>
          ) : (
            !timelineError && !isLoading && <div className="text-sm opacity-60">No status transitions recorded.</div>
          )}
        </div>
      </div>
    </TaskDetailShell>
  );
};

export default JiraTaskDetailPage;
